import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Image as ImageIcon, Trash2, FileText, Crop } from "lucide-react";

import { BACKEND } from "../../lib/backend";

export default function SlideInspector({
  selected,
  tiers,
  companies,
  bgFields,
  updateSlide,
  onSlideLogoUpload,
  pickFromCompany,
  uploadSlideBg,
  removeSlide,
  onOpenPdfImport,
  onOpenImageEditor,
}) {
  if (!selected) return null;

  return (
    <div className="space-y-4">
      <div className="label-eyebrow">Slide</div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Company name</Label>
        <Input
          data-testid="slide-company-name"
          value={selected.company_name}
          onChange={(e) => updateSlide({ company_name: e.target.value })}
          className="bg-zinc-950 border-zinc-800"
        />
      </div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Caption</Label>
        <Textarea
          data-testid="slide-caption"
          value={selected.caption || ""}
          onChange={(e) => updateSlide({ caption: e.target.value })}
          rows={3}
          className="bg-zinc-950 border-zinc-800"
        />
      </div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Logo</Label>
        <div className="flex items-center gap-2">
          <div className="w-12 h-12 rounded bg-zinc-950 border border-zinc-800 grid place-items-center overflow-hidden">
            {selected.image_url ? (
              <img src={`${BACKEND}${selected.image_url}`} alt="" className="w-full h-full object-contain" />
            ) : (
              <ImageIcon className="w-4 h-4 text-zinc-600" />
            )}
          </div>
          <Input data-testid="slide-logo-upload" type="file" accept="image/*,.pdf,.svg,.eps,.ai,.tiff,.tif,.heic,.heif,.ico,application/pdf,image/svg+xml" onChange={onSlideLogoUpload} className="bg-zinc-950 border-zinc-800 text-xs" />
        </div>
        <p className="text-[10px] text-zinc-500 -mt-1">
          PNG · JPG · WebP · GIF · SVG · TIFF · HEIC/HEIF · ICO · PDF · EPS · AI — vector & PDF files are auto-converted to PNG.
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {selected.image_url && onOpenImageEditor && (
            <button
              type="button"
              data-testid="slide-edit-image-inspector"
              onClick={() => onOpenImageEditor(selected.id)}
              className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1"
            >
              <Crop className="w-3 h-3" /> Edit image
            </button>
          )}
          {onOpenPdfImport && (
            <button
              type="button"
              data-testid="slide-import-pdf"
              onClick={() => onOpenPdfImport(selected.id)}
              className="text-xs text-zinc-300 hover:text-zinc-100 inline-flex items-center gap-1"
            >
              <FileText className="w-3 h-3" /> Import PDF as image
            </button>
          )}
          {selected.image_url && (
            <button onClick={() => updateSlide({ image_url: "" })} className="text-xs text-zinc-500 hover:text-red-400 ml-auto" data-testid="remove-slide-logo">
              Remove logo
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2 border-t border-zinc-800 pt-3">
        <Label className="label-eyebrow">
          Logo size · {((selected.logo_scale ?? 1).toFixed(2))}×
        </Label>
        <div className="flex items-center gap-2">
          <input
            data-testid="slide-logo-scale"
            type="range"
            min={0.3}
            max={3}
            step={0.05}
            value={selected.logo_scale ?? 1}
            onChange={(e) => updateSlide({ logo_scale: parseFloat(e.target.value) })}
            disabled={!selected.image_url}
            className="flex-1 accent-blue-500 disabled:opacity-50"
          />
          {selected.logo_scale != null && selected.logo_scale !== 1 && (
            <button
              type="button"
              data-testid="reset-logo-scale"
              onClick={() => updateSlide({ logo_scale: null })}
              className="text-[10px] text-zinc-500 hover:text-zinc-200 px-1"
            >
              reset
            </button>
          )}
        </div>
        {!selected.image_url && (
          <p className="text-[10px] text-zinc-600">Upload a logo to enable scaling.</p>
        )}
      </div>

      <div className="space-y-2 border-t border-zinc-800 pt-3">
        <div className="label-eyebrow">Show on slide</div>
        <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5">
          <div className="text-xs">Company name</div>
          <Switch
            data-testid="slide-show-company-name"
            checked={!selected.hide_company_name}
            onCheckedChange={(v) => updateSlide({ hide_company_name: !v })}
          />
        </div>
        <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5">
          <div className="text-xs">Caption</div>
          <Switch
            data-testid="slide-show-caption"
            checked={!selected.hide_caption}
            onCheckedChange={(v) => updateSlide({ hide_caption: !v })}
          />
        </div>
        <p className="text-[10px] text-zinc-500">Hide either to show only the logo (use a bigger Logo size for a clean splash-style slide).</p>
      </div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Tier</Label>
        <Select value={selected.tier_id || ""} onValueChange={(v) => updateSlide({ tier_id: v || null })}>
          <SelectTrigger data-testid="slide-tier-select" className="bg-zinc-950 border-zinc-800">
            <SelectValue placeholder="No tier" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            {tiers.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                <span className="inline-flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                  {t.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {companies.length > 0 && (
        <div className="space-y-2">
          <Label className="label-eyebrow">Quick-fill from saved company</Label>
          <Select onValueChange={pickFromCompany}>
            <SelectTrigger data-testid="slide-company-pick" className="bg-zinc-950 border-zinc-800">
              <SelectValue placeholder="Pick a company…" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2 border-t border-zinc-800 pt-3">
        <Label className="label-eyebrow">Slide background override</Label>
        <div className="flex items-center gap-2">
          <input
            data-testid="per-slide-bg-color"
            type="color"
            value={selected.background_color || bgFields.slide_background_color || "#0F0F11"}
            onChange={(e) => updateSlide({ background_color: e.target.value })}
            className="w-8 h-8 bg-transparent border border-zinc-800 rounded cursor-pointer"
          />
          <Input
            data-testid="per-slide-bg-color-text"
            value={selected.background_color || ""}
            onChange={(e) => updateSlide({ background_color: e.target.value || null })}
            placeholder="(use journal default)"
            className="bg-zinc-950 border-zinc-800 h-8 text-xs font-mono flex-1"
          />
          {selected.background_color && (
            <button
              type="button"
              data-testid="clear-per-slide-bg-color"
              onClick={() => updateSlide({ background_color: null })}
              className="text-[10px] text-zinc-500 hover:text-zinc-200 px-1"
            >
              clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selected.background_image_url ? (
            <>
              <img
                src={`${BACKEND}${selected.background_image_url}`}
                alt=""
                className="w-10 h-10 rounded object-cover border border-zinc-800"
              />
              <button
                type="button"
                data-testid="clear-per-slide-bg-image"
                onClick={() => updateSlide({ background_image_url: null })}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Remove image
              </button>
            </>
          ) : (
            <Input
              data-testid="per-slide-bg-image"
              type="file"
              accept="image/*"
              onChange={(e) => uploadSlideBg(e.target.files?.[0])}
              className="bg-zinc-950 border-zinc-800 text-xs"
            />
          )}
        </div>
        <p className="text-[10px] text-zinc-500">Per-slide override beats the journal-wide background. Useful for chapter dividers or feature slides.</p>
      </div>

      <div className="space-y-2 border-t border-zinc-800 pt-3">
        <Label className="label-eyebrow">
          Frame thickness · {(selected.frame_thickness_multiplier || 1).toFixed(1)}×
        </Label>
        <input
          data-testid="per-slide-frame-thickness"
          type="range"
          min={0.5}
          max={4}
          step={0.25}
          value={selected.frame_thickness_multiplier || 1}
          onChange={(e) => updateSlide({ frame_thickness_multiplier: parseFloat(e.target.value) })}
          className="w-full accent-blue-500"
        />
        {selected.frame_thickness_multiplier && selected.frame_thickness_multiplier !== 1 && (
          <button
            type="button"
            data-testid="reset-frame-thickness"
            onClick={() => updateSlide({ frame_thickness_multiplier: null })}
            className="text-[10px] text-zinc-500 hover:text-zinc-200"
          >
            Reset to default
          </button>
        )}
      </div>

      <Button
        variant="ghost"
        onClick={() => removeSlide(selected.id)}
        className="w-full text-red-400 hover:text-red-300 hover:bg-red-950/30"
        data-testid="remove-slide-btn"
      >
        <Trash2 className="w-4 h-4 mr-2" /> Delete slide
      </Button>
    </div>
  );
}
